/**
 * Builds the Three.js scene from world data using placeholder primitives
 * composited into small readable "kits" (stall = counter + posts + awning,
 * barrel/well = drum + rim, tower = base + platform + roof). Frontend agent:
 * swap for real models later — keep the `SceneHandles` shape stable so the
 * render loop in index.ts doesn't change.
 */
import * as THREE from 'three';
import type { Cable, GroundTextureConfig, ObjectId, Road, SpriteSpawn, WorldData, WorldObject } from '../types';
import { buildHumanoid } from './humanoid';
import { tint } from './color-utils';
import { groundUvMatrix } from './ground-uv';
import { buildGrassMesh, updateGrass as updateGrassMeshes } from './grass-mesh';
import { buildRoads } from './road-mesh';
import { buildCables } from './cable-mesh';
import { buildSpriteSpawns, type SpriteSpawnField } from './sprite-fx';
import { buildActorShadow, createBlobShadowAssets, type BlobShadowAssets } from './player-shadow';

export interface SceneHandles {
  scene: THREE.Scene;
  /** Player root — move/rotate this each frame from the sim snapshot. */
  player: THREE.Object3D;
  /** Root objects (mesh or group) carrying `userData.objectId`, for raycast picking. */
  pickables: THREE.Object3D[];
  objectMeshes: Map<string, THREE.Object3D>;
  /**
   * Every `kind: 'grass'` object's root — `updateGrass()` billboards exactly
   * these each frame. Exposed (not just a `buildScene()`-internal closure)
   * for the same reason `pickables`/`objectMeshes` are: a caller that spawns
   * a new object at runtime (`ultra_map_editor`'s `spawnGrass()`) pushes onto
   * it directly, the same way it pushes onto `pickables`.
   */
  grassMeshes: THREE.Object3D[];
  /**
   * The group holding every procedural road ribbon (see `render/road-mesh.ts`).
   * Exposed for the same reason `grassMeshes` is: `ultra_map_editor` edits
   * roads live and needs to rebuild them without a scene rebuild.
   */
  roads: THREE.Group;
  /**
   * Rebuild every road ribbon from scratch. Roads are generated geometry, not
   * transformed instances — moving one point changes the whole ribbon's
   * vertices and arc-length UVs — so there is nothing to update in place and
   * a full rebuild is the honest operation. Editor-only; the live game builds
   * once from `WorldData.roads`.
   */
  setRoads(roads: readonly Road[] | undefined): void;
  /** The group holding every hanging cable — see `render/cable-mesh.ts`. */
  cables: THREE.Group;
  /**
   * Rebuild every cable. Like roads, a cable is generated geometry: moving one
   * anchor re-samples the whole droop, so there is nothing to update in place.
   */
  setCables(cables: readonly Cable[] | undefined): void;
  /**
   * Hand-placed fire/smoke billboards — see `WorldData.spriteSpawns`. Exposed
   * as the whole field rather than a bare group because, unlike a road or a
   * cable, these ANIMATE: the caller owns the frame loop, so it has to be able
   * to reach both the sprites (to move or retint one) and `update`.
   */
  spriteSpawns: SpriteSpawnField;
  /**
   * Rebuild every authored billboard. Needed when a spawn is added, deleted,
   * or has its sheet swapped; moving one or changing its size/tint is done in
   * place through `spriteSpawns.sprites` instead, since a rebuild re-clones
   * the sheet texture and restarts the animation mid-drag.
   */
  setSpriteSpawns(spawns: readonly SpriteSpawn[] | undefined): void;
  /** Toggle the hover highlight on a pickable object; `inRange` gives a stronger glow. */
  setHovered(id: ObjectId | null, inRange: boolean): void;
  /**
   * Swap the ground's blend layers in place — no scene rebuild. Re-reads a
   * layer's image only when its URL actually changed, so dragging a scale or
   * rotation field is a uniform update, not three texture loads per keystroke.
   * `ultra_map_editor` calls this for live preview while the user tunes the
   * layers; the live game never calls it (whatever's baked into
   * `WorldData.groundTexture` is what `buildScene` applies up front).
   */
  setGroundTexture(cfg: GroundTextureConfig | undefined): void;
  /**
   * Editor-only: replace the ground with a flat, unlit view of the blend mask
   * alone, so the noise texture's scale/rotation can be judged directly
   * instead of inferred from the blend. Never called by the live game.
   */
  setGroundNoisePreview(on: boolean): void;
  /**
   * Swaps `scene.environment` (image-based lighting — reflections/ambient on
   * every `MeshStandardMaterial` surface), independent of `scene.background`,
   * which this never touches. `url` undefined clears it back to no
   * environment map (today's behaviour). See `WorldData.environment`'s doc
   * comment for the asset requirement and the PMREM tradeoff. Live-callable
   * (not just at load) so `ultra_map_editor`'s World panel can preview
   * changes the same way `setGroundTexture` does.
   */
  setEnvironmentMap(url: string | undefined, intensity: number | undefined): void;
  /** Per-frame ambient firelight flicker. Pass the user's motion preference. */
  updateAmbience(dt: number, reducedMotion: boolean): void;
  /**
   * Per-frame grass upkeep: advances the shared wind-sway uniform and
   * billboards every `kind: 'grass'` object to face the camera (Y-axis only
   * — see `render/grass-mesh.ts`). `cameraYaw` uses the same convention as
   * `WorldObject.rotationY` (0 = facing +Z) — pass `CameraController.yawValue`
   * in the live game, or the equivalent computed from the editor's free
   * orbit camera.
   */
  updateGrass(dt: number, cameraYaw: number): void;
  dispose(): void;
}

const PLAYER_COLOR = 0xb85a38;
/**
 * Hover glow strength, as `emissiveIntensity` on every mesh under the hovered
 * root (see `setHovered`).
 *
 * Janeks, on the old 0.5: *"Change the highlight on mouse over, make it 10% of
 * opacity."*
 *
 * This number is NOT an opacity, and 0.1 is not 10% of anything the player can
 * see — the glow is an EMISSIVE, added to the lit result rather than blended
 * over it, and the settlement is lit dimly enough that a little emissive goes a
 * long way. So the value was picked by measuring the rendered frame instead of
 * by reading the constant: hovering the same wreck, screenshotting a patch of
 * it hovered and not, and taking how far its mean colour travels toward white.
 *
 *   0.5  (before) -> ~40% toward white   <- "too strong"
 *   0.1           -> ~15%
 *   0.05          -> ~10%                <- what was asked for
 *
 * `IN_RANGE` is the stronger glow that says "close enough to interact". It was
 * not named in the request, so it keeps its former RATIO to the plain hover
 * (0.85 / 0.5 = 1.7) rather than a number invented here. At these levels the
 * two are nearly indistinguishable, which is survivable only because in-range
 * is also signalled by the cursor (`bv-hover-interactable`) and by the interact
 * prompt — the glow was never carrying that alone.
 */
const HOVER_INTENSITY = 0.05;
const HOVER_INTENSITY_IN_RANGE = 0.085;
const GROUND_PLACEHOLDER_COLOR = 0x2b2216;

// Per-layer defaults for `GroundTextureConfig` — exported so ultra_map_editor's
// world panel shows/starts from the same numbers this renderer falls back to
// when a field is omitted. The mask defaults far coarser than the two ground
// textures on purpose: large blotches are what break up their tiling, a mask
// tiled as tightly as the textures themselves just adds a fourth repeat.
export const DEFAULT_GROUND_TEXTURE_SCALE = 0.15;
export const DEFAULT_GROUND_TEXTURE_ROTATION = 0;
export const DEFAULT_GROUND_NOISE_SCALE = 0.05;
// Off-axis by default so the mask's tile grid does not line up with the two
// ground textures' — three grids sharing an orientation still read as one.
export const DEFAULT_GROUND_NOISE_ROTATION = 0.4;
export const DEFAULT_GROUND_NOISE_OFFSET = 0;
export const DEFAULT_GROUND_NOISE_GAIN = 1;
// Anisotropic filtering keeps the tiled ground sharp at the shallow, near-
// grazing camera angle CameraConfig uses (55° pitch) — cheap, GPU-clamped.
const GROUND_TEXTURE_ANISOTROPY = 8;

const groundTextureLoader = new THREE.TextureLoader();

/**
 * Stands in for an unset layer. The ground shader samples all three layers
 * unconditionally — branching on uniforms rather than `#define`s, so dragging
 * a slider in the editor never recompiles the program — and an unbound
 * sampler is undefined behaviour. Shared and module-scoped, so it must NEVER
 * be disposed: `disposeGroundSlots` frees only textures it loaded itself.
 */
// Typed as the base `Texture` so the uniforms below accept a loaded texture
// too — inferring `DataTexture` here would pin the slot to that subclass.
const FALLBACK_GROUND_TEXTURE: THREE.Texture = (() => {
  const tex = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
  tex.needsUpdate = true;
  return tex;
})();

/**
 * One ground blend layer. `url` is the cache key: `setSlot` reloads only on a
 * real URL change, which is what makes scale/rotation tuning free.
 */
interface GroundSlot {
  url: string | null;
  tex: THREE.Texture | null;
}

function loadGroundTexture(url: string, srgb: boolean): THREE.Texture {
  const tex = groundTextureLoader.load(url);
  // Tiling lives in the injected UV matrix, not in `repeat` (see ground-uv.ts)
  // — but the sampler still has to wrap, or every layer clamps to one stretched
  // tile with smeared borders.
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = GROUND_TEXTURE_ANISOTROPY;
  // The two ground textures are colour and must be decoded; the mask is a
  // blend factor, and decoding it as sRGB would move the 50%-grey midpoint.
  if (srgb) tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function setSlot(slot: GroundSlot, url: string | undefined, srgb: boolean): void {
  const next = url ?? null;
  if (slot.url === next) return;
  slot.tex?.dispose();
  slot.url = next;
  slot.tex = next ? loadGroundTexture(next, srgb) : null;
}

// GLSL injected into MeshStandardMaterial to blend the two ground textures by
// the mask. Split in two because three's fragment shader needs the uniform
// declarations up top and the sampling down inside main().
const GROUND_BLEND_PARS = /* glsl */ `
uniform sampler2D uBaseMap;
uniform sampler2D uSecondaryMap;
uniform sampler2D uNoiseMap;
uniform mat3 uBaseUv;
uniform mat3 uSecondaryUv;
uniform mat3 uNoiseUv;
uniform float uHasGroundTexture;
uniform float uBlend;
uniform float uNoiseOffset;
uniform float uNoiseGain;
`;

const GROUND_BLEND_FRAGMENT = /* glsl */ `
if ( uHasGroundTexture > 0.5 ) {
	vec3 baseColor = texture2D( uBaseMap, ( uBaseUv * vec3( vUv, 1.0 ) ).xy ).rgb;
	vec3 secondaryColor = texture2D( uSecondaryMap, ( uSecondaryUv * vec3( vUv, 1.0 ) ).xy ).rgb;
	float rawMask = texture2D( uNoiseMap, ( uNoiseUv * vec3( vUv, 1.0 ) ).xy ).g;
	// Gain scales contrast around the 0.5 midpoint (so it sharpens/flattens the
	// blotches rather than just brightening them), offset then shifts the whole
	// result toward base or secondary before clamping back into a valid mix factor.
	float mask = clamp( ( rawMask - 0.5 ) * uNoiseGain + 0.5 + uNoiseOffset, 0.0, 1.0 );
	// mix( a, b, t ) returns a at t = 0 — so WHITE mask (t = 1) must select the
	// base and BLACK the secondary, matching the field names. uBlend is 0
	// unless all three layers are set, which pins the factor at 1 (pure base)
	// instead of blending against the white fallback texture.
	diffuseColor.rgb *= mix( secondaryColor, baseColor, mix( 1.0, mask, uBlend ) );
}
`;

// Shader-source edits made in onBeforeCompile are invisible to three's program
// cache, which keys on defines/parameters. Every placeholder kit below is also
// a MeshStandardMaterial, so without an explicit key one of them could be
// handed the ground's mutated program.
const GROUND_PROGRAM_CACHE_KEY = 'after-humanity-ground-blend';

const NOISE_PREVIEW_VERTEX = /* glsl */ `
varying vec2 vUv;
void main() {
	vUv = uv;
	gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
}
`;

// Unlit and undecoded on purpose: this shows the mask exactly as the blend
// above consumes it, so what you tune is what you see. Sharing the mask's
// uniform objects by reference (not by copy) is what keeps the two in step.
const NOISE_PREVIEW_FRAGMENT = /* glsl */ `
uniform sampler2D uNoiseMap;
uniform mat3 uNoiseUv;
uniform float uNoiseOffset;
uniform float uNoiseGain;
varying vec2 vUv;
void main() {
	float rawMask = texture2D( uNoiseMap, ( uNoiseUv * vec3( vUv, 1.0 ) ).xy ).g;
	float mask = clamp( ( rawMask - 0.5 ) * uNoiseGain + 0.5 + uNoiseOffset, 0.0, 1.0 );
	gl_FragColor = vec4( vec3( mask ), 1.0 );
}
`;

// ---------------------------------------------------------------------------
// Object "kits" — small composite placeholders built from primitives only.
// ---------------------------------------------------------------------------

function partMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.85,
    metalness: 0.05,
    emissive: tint(color, 1.7),
    emissiveIntensity: 0,
  });
}

function markPart<T extends THREE.Mesh>(mesh: T, baseEmissive = 0): T {
  mesh.userData.baseEmissiveIntensity = baseEmissive;
  return mesh;
}

function buildStall(obj: WorldObject): THREE.Object3D {
  const group = new THREE.Group();
  const counterH = 1.05;
  const postH = 2.05;
  const postR = 0.07;

  const counter = markPart(
    new THREE.Mesh(new THREE.BoxGeometry(obj.size.w, counterH, obj.size.d * 0.55), partMaterial(obj.color)),
  );
  counter.position.set(0, counterH / 2, obj.size.d * 0.22);
  group.add(counter);

  const postMat = partMaterial(tint(obj.color, 0.4));
  const insetW = obj.size.w / 2 - postR;
  const insetD = obj.size.d / 2 - postR;
  const corners: Array<[number, number]> = [
    [-insetW, -insetD],
    [insetW, -insetD],
    [-insetW, insetD],
    [insetW, insetD],
  ];
  for (const [cx, cz] of corners) {
    const post = markPart(new THREE.Mesh(new THREE.CylinderGeometry(postR, postR, postH, 8), postMat));
    post.position.set(cx, postH / 2, cz);
    group.add(post);
  }

  const awning = markPart(
    new THREE.Mesh(
      new THREE.BoxGeometry(obj.size.w * 1.15, 0.12, obj.size.d * 1.1),
      partMaterial(tint(obj.color, 1.4)),
    ),
  );
  awning.position.set(0, postH, 0);
  awning.rotation.x = -0.12;
  group.add(awning);

  return group;
}

function buildTower(obj: WorldObject): THREE.Object3D {
  const group = new THREE.Group();

  const base = markPart(new THREE.Mesh(new THREE.BoxGeometry(obj.size.w, obj.size.h, obj.size.d), partMaterial(obj.color)));
  base.position.y = obj.size.h / 2;
  group.add(base);

  const platform = markPart(
    new THREE.Mesh(new THREE.BoxGeometry(obj.size.w * 1.35, 0.18, obj.size.d * 1.35), partMaterial(tint(obj.color, 0.6))),
  );
  platform.position.y = obj.size.h + 0.1;
  group.add(platform);

  const roof = markPart(
    new THREE.Mesh(new THREE.ConeGeometry(obj.size.w * 0.85, 1.4, 4), partMaterial(tint(obj.color, 1.25))),
  );
  roof.rotation.y = Math.PI / 4;
  roof.position.y = obj.size.h + 0.1 + 0.7;
  group.add(roof);

  return group;
}

function buildGate(obj: WorldObject): THREE.Object3D {
  const group = new THREE.Group();
  const postW = 0.5;
  const postH = obj.size.h;
  const postMat = partMaterial(tint(obj.color, 0.5));

  const insetW = obj.size.w / 2 - postW / 2;
  for (const cx of [-insetW, insetW]) {
    const post = markPart(new THREE.Mesh(new THREE.BoxGeometry(postW, postH, obj.size.d), postMat));
    post.position.set(cx, postH / 2, 0);
    group.add(post);
  }

  const crossbar = markPart(
    new THREE.Mesh(new THREE.BoxGeometry(obj.size.w, 0.4, obj.size.d * 0.8), partMaterial(tint(obj.color, 1.2))),
  );
  crossbar.position.set(0, postH - 0.2, 0);
  group.add(crossbar);

  return group;
}

function buildBarrelOrWell(obj: WorldObject): THREE.Object3D {
  const group = new THREE.Group();

  const body = markPart(
    new THREE.Mesh(new THREE.CylinderGeometry(obj.size.w / 2, obj.size.w / 2, obj.size.h, 20), partMaterial(obj.color)),
  );
  body.position.y = obj.size.h / 2;
  group.add(body);

  const rim = markPart(
    new THREE.Mesh(
      new THREE.TorusGeometry(obj.size.w / 2, Math.min(0.06, obj.size.w * 0.08), 8, 20),
      partMaterial(tint(obj.color, 0.55)),
    ),
  );
  rim.rotation.x = Math.PI / 2;
  rim.position.y = obj.size.h * 0.82;
  group.add(rim);

  return group;
}

function buildCrate(obj: WorldObject): THREE.Object3D {
  const group = new THREE.Group();
  const mesh = markPart(new THREE.Mesh(new THREE.BoxGeometry(obj.size.w, obj.size.h, obj.size.d), partMaterial(obj.color)));
  mesh.position.y = obj.size.h / 2;
  group.add(mesh);
  return group;
}

function buildObjectVisual(obj: WorldObject): THREE.Object3D {
  // 'grass' is the one kind that's never a placeholder kit standing in for a
  // pending GLB — it's the actual, final, procedural visual (see
  // grass-mesh.ts), so it's checked before the `modelUrl` branch even though
  // a grass object never has one.
  if (obj.kind === 'grass') return buildGrassMesh(obj);
  // A real model replaces the kit entirely — the caller (index.ts) loads it
  // async and adds it as a child of this (empty, but already pickable/
  // positioned) group once ready.
  if (obj.modelUrl) return new THREE.Group();
  if (obj.kind === 'stall') return buildStall(obj);
  if (obj.kind === 'building') return buildTower(obj);
  if (obj.kind === 'gate') return buildGate(obj);
  if (obj.round) return buildBarrelOrWell(obj);
  return buildCrate(obj);
}

// ---------------------------------------------------------------------------

export function buildScene(world: WorldData, playerModel?: THREE.Object3D): SceneHandles {
  const scene = new THREE.Scene();
  // Per-map overrides (see `WorldData.lighting`); every playable map takes the
  // defaults, which are the dusk-firelight look.
  const lit = world.lighting;
  const bg = lit?.background ?? 0x120d09;
  scene.background = new THREE.Color(bg);
  if (lit?.fog !== false) scene.fog = new THREE.Fog(bg, 28, 74);

  // ---- lighting: warm firelight key + cool fill ----
  const fill = new THREE.HemisphereLight(0x8fb6c4, lit?.fillGround ?? 0x140d08, lit?.fill ?? 0.5);
  scene.add(fill);
  const key = new THREE.DirectionalLight(0xffb27a, lit?.key ?? 0.9);
  key.position.set(14, 20, 10);
  scene.add(key);
  const campfire = new THREE.PointLight(0xffae5c, lit?.campfire ?? 1.3, 30, 2);
  campfire.position.set(0, 2.4, 0);
  scene.add(campfire);
  const baseFireIntensity = campfire.intensity;

  // ---- ground: two tiling textures blended by a third used as a mask ----
  // One lit plane, one draw call. The blend is injected into
  // MeshStandardMaterial's fragment shader rather than layered as a second
  // transparent plane, so it keeps scene lighting and fog exactly as an
  // untextured ground would, and never competes with the decals at y = 0.01
  // for transparent-sort order.
  const groundUniforms = {
    uBaseMap: { value: FALLBACK_GROUND_TEXTURE },
    uSecondaryMap: { value: FALLBACK_GROUND_TEXTURE },
    uNoiseMap: { value: FALLBACK_GROUND_TEXTURE },
    uBaseUv: { value: new THREE.Matrix3() },
    uSecondaryUv: { value: new THREE.Matrix3() },
    uNoiseUv: { value: new THREE.Matrix3() },
    uHasGroundTexture: { value: 0 },
    uBlend: { value: 0 },
    uNoiseOffset: { value: DEFAULT_GROUND_NOISE_OFFSET },
    uNoiseGain: { value: DEFAULT_GROUND_NOISE_GAIN },
  };

  const groundGeo = new THREE.PlaneGeometry(world.ground.width, world.ground.depth);
  const groundMat = new THREE.MeshStandardMaterial({ color: GROUND_PLACEHOLDER_COLOR, roughness: 1 });
  // three only declares `vUv` when a built-in map slot asks for it. The ground
  // fills none of them (all three layers are sampled by the injected chunk, on
  // their own UV matrices), so request it explicitly.
  groundMat.defines = { ...(groundMat.defines ?? {}), USE_UV: '' };
  groundMat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, groundUniforms);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${GROUND_BLEND_PARS}`)
      .replace('#include <map_fragment>', GROUND_BLEND_FRAGMENT);
  };
  groundMat.customProgramCacheKey = () => GROUND_PROGRAM_CACHE_KEY;
  const ground = new THREE.Mesh(groundGeo, groundMat);
  ground.rotation.x = -Math.PI / 2;
  scene.add(ground);

  // ---- mask preview plane (editor only, hidden by default) ----
  // Sits above the grid (0.01) so nothing draws over it, and is opaque, so it
  // simply covers the ground when shown.
  const noisePreviewMat = new THREE.ShaderMaterial({
    uniforms: {
      uNoiseMap: groundUniforms.uNoiseMap,
      uNoiseUv: groundUniforms.uNoiseUv,
      uNoiseOffset: groundUniforms.uNoiseOffset,
      uNoiseGain: groundUniforms.uNoiseGain,
    },
    vertexShader: NOISE_PREVIEW_VERTEX,
    fragmentShader: NOISE_PREVIEW_FRAGMENT,
  });
  const noisePreview = new THREE.Mesh(
    new THREE.PlaneGeometry(world.ground.width, world.ground.depth),
    noisePreviewMat,
  );
  noisePreview.rotation.x = -Math.PI / 2;
  noisePreview.position.y = 0.02;
  noisePreview.visible = false;
  scene.add(noisePreview);

  const groundSlots: Record<'base' | 'secondary' | 'noise', GroundSlot> = {
    base: { url: null, tex: null },
    secondary: { url: null, tex: null },
    noise: { url: null, tex: null },
  };

  const setGroundTexture = (cfg: GroundTextureConfig | undefined): void => {
    setSlot(groundSlots.base, cfg?.textureUrl, true);
    setSlot(groundSlots.secondary, cfg?.secondaryUrl, true);
    setSlot(groundSlots.noise, cfg?.noiseUrl, false);

    // With only one of the two ground textures set, that one covers the whole
    // ground: both slots resolve to it and the mask is ignored (uBlend = 0).
    // Without this, setting *only* a secondary would render the bare
    // placeholder colour and read as a broken texture picker.
    const baseFirst = groundSlots.base.tex ? 'base' : 'secondary';
    const secondFirst = groundSlots.secondary.tex ? 'secondary' : 'base';
    const baseSrc = groundSlots[baseFirst];
    const secondarySrc = groundSlots[secondFirst];

    groundUniforms.uBaseMap.value = baseSrc.tex ?? FALLBACK_GROUND_TEXTURE;
    groundUniforms.uSecondaryMap.value = secondarySrc.tex ?? FALLBACK_GROUND_TEXTURE;
    groundUniforms.uNoiseMap.value = groundSlots.noise.tex ?? FALLBACK_GROUND_TEXTURE;

    const w = world.ground.width;
    const d = world.ground.depth;
    const baseScale = (baseFirst === 'base' ? cfg?.textureScale : cfg?.secondaryScale) ?? DEFAULT_GROUND_TEXTURE_SCALE;
    const baseRot = (baseFirst === 'base' ? cfg?.textureRotation : cfg?.secondaryRotation) ?? DEFAULT_GROUND_TEXTURE_ROTATION;
    const secScale = (secondFirst === 'secondary' ? cfg?.secondaryScale : cfg?.textureScale) ?? DEFAULT_GROUND_TEXTURE_SCALE;
    const secRot = (secondFirst === 'secondary' ? cfg?.secondaryRotation : cfg?.textureRotation) ?? DEFAULT_GROUND_TEXTURE_ROTATION;

    // Mutated in place — the uniform holds the same Matrix3 the shader reads,
    // and three re-uploads it once its elements actually change.
    groundUniforms.uBaseUv.value.fromArray(groundUvMatrix(w, d, baseScale, baseRot));
    groundUniforms.uSecondaryUv.value.fromArray(groundUvMatrix(w, d, secScale, secRot));
    groundUniforms.uNoiseUv.value.fromArray(
      groundUvMatrix(w, d, cfg?.noiseScale ?? DEFAULT_GROUND_NOISE_SCALE, cfg?.noiseRotation ?? DEFAULT_GROUND_NOISE_ROTATION),
    );

    groundUniforms.uNoiseOffset.value = cfg?.noiseOffset ?? DEFAULT_GROUND_NOISE_OFFSET;
    groundUniforms.uNoiseGain.value = cfg?.noiseGain ?? DEFAULT_GROUND_NOISE_GAIN;

    groundUniforms.uHasGroundTexture.value = baseSrc.tex ? 1 : 0;
    // Blending needs all three layers — two textures and something to mix them by.
    groundUniforms.uBlend.value = groundSlots.base.tex && groundSlots.secondary.tex && groundSlots.noise.tex ? 1 : 0;
    // Let the textures drive colour; the placeholder tint would multiply into them.
    groundMat.color.set(baseSrc.tex ? 0xffffff : (world.lighting?.ground ?? GROUND_PLACEHOLDER_COLOR));
  };
  setGroundTexture(world.groundTexture);

  const setGroundNoisePreview = (on: boolean): void => {
    noisePreview.visible = on;
  };

  // ---- environment map (IBL): optional, off by default — see types.ts ----
  const environmentLoader = new THREE.TextureLoader();
  let environmentTex: THREE.Texture | null = null;

  const setEnvironmentMap = (url: string | undefined, intensity: number | undefined): void => {
    environmentTex?.dispose();
    environmentTex = null;
    if (!url) {
      scene.environment = null;
      scene.environmentIntensity = 1;
      return;
    }
    const tex = environmentLoader.load(url);
    tex.mapping = THREE.EquirectangularReflectionMapping;
    tex.colorSpace = THREE.SRGBColorSpace;
    environmentTex = tex;
    scene.environment = tex;
    scene.environmentIntensity = intensity ?? 1;
  };
  setEnvironmentMap(world.environment?.url, world.environment?.intensity);

  // ---- roads: generated ribbons along authored polylines ----
  // Added before the objects loop so they sit under everything placed on
  // them, and lifted only 5 mm (see road-mesh.ts's ROAD_Y) so a Decal at
  // 0.01 still draws on top.
  let roadGroup = buildRoads(world.roads);
  scene.add(roadGroup);

  const disposeRoads = (group: THREE.Group): void => {
    group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      mesh.geometry?.dispose();
      const mat = mesh.material as THREE.MeshStandardMaterial | undefined;
      // Each road loads its own texture (no shared cache, like decal-mesh.ts),
      // so freeing it here is safe and necessary.
      mat?.map?.dispose();
      mat?.dispose();
    });
    group.removeFromParent();
  };

  const setRoads = (roads: readonly Road[] | undefined): void => {
    disposeRoads(roadGroup);
    roadGroup = buildRoads(roads);
    scene.add(roadGroup);
  };

  // ---- cables: hanging power lines over the settlement ----
  let cableGroup = buildCables(world.cables);
  scene.add(cableGroup);

  const disposeCables = (group: THREE.Group): void => {
    group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      mesh.geometry?.dispose();
      (mesh.material as THREE.Material | undefined)?.dispose();
    });
    group.removeFromParent();
  };

  const setCables = (cables: readonly Cable[] | undefined): void => {
    disposeCables(cableGroup);
    cableGroup = buildCables(cables);
    scene.add(cableGroup);
  };

  // ---- authored sprite VFX: hand-placed fire/smoke billboards ----
  // Built here rather than by the caller so the editor gets them from
  // `buildScene` for free, exactly like roads and cables — same "generated
  // content the editor rebuilds live" shape.
  let spriteSpawnField = buildSpriteSpawns(world.spriteSpawns);
  scene.add(spriteSpawnField.object);

  const setSpriteSpawns = (spawns: readonly SpriteSpawn[] | undefined): void => {
    spriteSpawnField.dispose();
    spriteSpawnField = buildSpriteSpawns(spawns);
    scene.add(spriteSpawnField.object);
  };

  const grid = new THREE.GridHelper(world.ground.width, world.ground.width / 2, 0x4a3a24, 0x33281a);
  (grid.material as THREE.Material).transparent = true;
  (grid.material as THREE.Material).opacity = 0.35;
  grid.position.y = 0.01;
  scene.add(grid);

  // ---- world objects (composited placeholder kits) ----
  const pickables: THREE.Object3D[] = [];
  const objectMeshes = new Map<string, THREE.Object3D>();
  // Tracked separately (not derived from `objectMeshes` every frame) so
  // `updateGrass()` can iterate exactly the roots that need billboarding
  // without a kind lookup or a scene traversal each frame.
  const grassMeshes: THREE.Object3D[] = [];

  // Lazily built, because a map with no bodies on it (a raid street) should
  // not pay for a texture load nothing will use.
  let blobShadowAssets: BlobShadowAssets | null = null;

  for (const obj of world.objects) {
    const root = buildObjectVisual(obj);
    root.position.set(obj.position.x, obj.positionY ?? 0, obj.position.z);
    // Harmless for 'grass' even though its rotation is meaningless
    // (updateGrass() overwrites it every frame regardless of this initial
    // value) — not worth a branch to skip it. Order: YXZ to avoid gimbal lock.
    root.rotation.order = 'YXZ';
    root.rotation.y = obj.rotationY ?? 0;
    root.rotation.x = obj.rotationX ?? 0;
    root.rotation.z = obj.rotationZ ?? 0;
    root.userData.objectId = obj.id;
    root.userData.interactable = obj.interactable;
    scene.add(root);
    pickables.push(root);
    objectMeshes.set(obj.id, root);
    if (obj.kind === 'grass') grassMeshes.push(root);
    // Parented to the root rather than driven from the frame loop — the root
    // is what `syncActors` already moves, yaws and hides, so the shadow is
    // incapable of lagging the body. See `buildActorShadow`.
    if (obj.kind === 'npc') {
      blobShadowAssets ??= createBlobShadowAssets();
      root.add(buildActorShadow(blobShadowAssets));
    }
  }

  // ---- player: loaded GLB model or fallback blocky humanoid ----
  const player = new THREE.Group();
  if (playerModel) {
    // Cloned from `character-select/load-model.ts`'s module-level cache,
    // which every future clone (this mount's NPC bodies included) shares
    // geometry/material with. Flagged `sharedAsset` so dispose() below
    // detaches rather than frees it — freeing it here would corrupt the
    // cache for the next mount (SPA sign-out -> sign-in reuses the module).
    playerModel.userData.sharedAsset = true;
    player.add(playerModel);
  } else {
    const humanoid = buildHumanoid(PLAYER_COLOR);
    const indicator = new THREE.Mesh(
      new THREE.ConeGeometry(0.11, 0.26, 8),
      new THREE.MeshStandardMaterial({ color: 0xffcf9a, emissive: 0xffb35c, emissiveIntensity: 0.4, roughness: 0.4 }),
    );
    indicator.rotation.x = Math.PI / 2;
    indicator.position.set(0, 0.95, 0.3);
    player.add(humanoid.group, indicator);
  }
  scene.add(player);

  // ---- hover highlight ----
  let hoveredId: ObjectId | null = null;
  let hoveredInRange = false;

  const applyEmissive = (root: THREE.Object3D, value: number | null): void => {
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.geometry || Array.isArray(mesh.material)) return;
      const mat = mesh.material as THREE.MeshStandardMaterial;
      // A blob shadow's MeshBasicMaterial has no emissive channel — and it is
      // SHARED by every NPC in the scene, so writing an intensity onto it
      // would be a no-op that nonetheless reaches well outside the body being
      // hovered. Anything without an emissive is not part of the glow.
      if (!mat?.emissive) return;
      const base = (mesh.userData.baseEmissiveIntensity as number | undefined) ?? 0;
      mat.emissiveIntensity = value === null ? base : value;
    });
  };

  const setHovered = (id: ObjectId | null, inRange: boolean): void => {
    if (id === hoveredId && inRange === hoveredInRange) return;
    if (hoveredId) {
      const prev = objectMeshes.get(hoveredId);
      if (prev) applyEmissive(prev, null);
    }
    if (id) {
      const next = objectMeshes.get(id);
      if (next) applyEmissive(next, inRange ? HOVER_INTENSITY_IN_RANGE : HOVER_INTENSITY);
    }
    hoveredId = id;
    hoveredInRange = inRange;
  };

  // ---- ambience: subtle firelight flicker, gated by motion preference ----
  let ambienceT = 0;
  const updateAmbience = (dt: number, reducedMotion: boolean): void => {
    if (reducedMotion) {
      campfire.intensity = baseFireIntensity;
      return;
    }
    ambienceT += dt;
    const flicker =
      Math.sin(ambienceT * 8.3) * 0.05 + Math.sin(ambienceT * 17.1 + 1.7) * 0.03 + (Math.random() - 0.5) * 0.04;
    campfire.intensity = baseFireIntensity + flicker;
  };

  const updateGrass = (dt: number, cameraYaw: number): void => {
    updateGrassMeshes(grassMeshes, dt, cameraYaw);
  };

  const dispose = (): void => {
    // Each billboard owns a CLONE of its sheet texture (see `loadSheet`), and
    // the generic sweep below only frees `material.map` on meshes — a Sprite's
    // map would leak one GPU texture per placed fire across every map reload
    // the editor does.
    spriteSpawnField.dispose();
    // The ground's three layers hang off uniforms, not off `material.map`, so
    // the generic material sweep below cannot see them. Freed here — and only
    // the ones actually loaded, never the shared fallback texel.
    for (const slot of Object.values(groundSlots)) {
      slot.tex?.dispose();
      slot.tex = null;
      slot.url = null;
    }
    environmentTex?.dispose();
    environmentTex = null;

    // Detach shared-asset subtrees (player model + NPC bodies, both cloned
    // from `load-model.ts`'s cache) BEFORE the generic traversal below — their
    // geometry/material are the same object references the cache (and every
    // other clone of that URL) holds, so disposing them here would corrupt
    // the next mount's clones. Collected first because mutating the graph
    // (removeFromParent) inside `scene.traverse` would skip siblings.
    const shared: THREE.Object3D[] = [];
    scene.traverse((o) => {
      if (o.userData.sharedAsset) shared.push(o);
    });
    for (const o of shared) o.removeFromParent();

    scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      const m = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (!m) return;
      const mats = Array.isArray(m) ? m : [m];
      for (const mat of mats) {
        const std = mat as THREE.MeshStandardMaterial;
        std.map?.dispose();
        std.normalMap?.dispose();
        std.roughnessMap?.dispose();
        std.metalnessMap?.dispose();
        std.emissiveMap?.dispose();
        std.aoMap?.dispose();
        mat.dispose();
      }
    });
    scene.clear();
  };

  return {
    scene, player, pickables, objectMeshes, grassMeshes, setHovered,
    get roads() { return roadGroup; },
    setRoads,
    get cables() { return cableGroup; },
    setCables,
    get spriteSpawns() { return spriteSpawnField; },
    setSpriteSpawns,
    setGroundTexture, setGroundNoisePreview, setEnvironmentMap, updateAmbience, updateGrass, dispose,
  };
}
